import { Badge, Card, Field } from '@/components/ui/page';
import { btn } from '@/components/ui/styles';
import { formatDateTime, formatRelative } from '@/utils/format';
import { useApi } from '@/utils/use-api';

import SettingsHeader from './settings-nav';

/** GET /sync/owner-hub (backend/pkg/api/ownerhub_sync.go, SyncStatus; data.SyncRun). */
type SyncRun = {
  source: string;
  started_at: string;
  finished_at: string;
  ok: boolean;
  error?: string;
  stats: Record<string, number | string | boolean> | null;
};
type SyncStatus = { source: string; enabled: boolean; last_run: SyncRun | null; last_success: SyncRun | null };

const STAT_LABELS: Record<string, string> = {
  owners: 'Owners read',
  leads_created: 'Leads created',
  leads_updated: 'Leads updated',
  casinos: 'Casinos',
  conflicts: 'Conflicts (one email, two accounts)',
  skipped: 'Skipped (another replica ran it)',
};

const tookMs = (r: SyncRun) => Math.max(0, new Date(r.finished_at).getTime() - new Date(r.started_at).getTime());
const formatTook = (ms: number) => (ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(1)} s`);

const When = ({ run }: { run: SyncRun }) => (
  <>
    {formatDateTime(run.started_at)} <span className="text-fg-faint">({formatRelative(run.started_at)})</span>
  </>
);

/**
 * Owner Hub sync (CAS-2, LEAD-1, LEAD-4): whether Prisma reads Owner Hub, and how its
 * last run and last successful run went. Real data from GET /sync/owner-hub.
 */
const SyncSettingsPage = () => {
  const { data, error, loading, reload } = useApi<SyncStatus>('/sync/owner-hub');
  const run = data?.last_run ?? null;
  const success = data?.last_success ?? null;
  const stats = success?.stats ? Object.entries(success.stats) : [];

  return (
    <div className="max-w-4xl">
      <SettingsHeader />

      <div className="space-y-4">
        <Card
          title="Owner Hub sync"
          aside={
            <button type="button" className={btn.quiet} onClick={reload} disabled={loading}>
              Refresh
            </button>
          }
        >
          <p className="mb-3 text-sm text-fg-muted">
            Every few minutes Prisma reads Owner Hub, read-only (its database, or its internal API where Prisma has no database role), and updates leads and casinos from it. Nobody enters a casino by hand
            (CAS-2).
          </p>
          {error && !data ? (
            <p className="text-sm text-danger">{error}</p>
          ) : (
            <dl>
              <Field label="Status">{data ? data.enabled ? <Badge tone="ok">On</Badge> : <Badge>Off: neither owner-hub-dsn nor Owner Hub's API is set</Badge> : '—'}</Field>
              <Field label="Last run">
                {run ? (
                  <span className="flex flex-wrap items-center gap-2">
                    <span>
                      <When run={run} />
                    </span>
                    <Badge tone={run.ok ? 'ok' : 'danger'}>{run.ok ? 'OK' : 'Failed'}</Badge>
                    <span className="text-xs text-fg-faint">took {formatTook(tookMs(run))}</span>
                  </span>
                ) : data ? (
                  'Never'
                ) : (
                  '—'
                )}
              </Field>
              {run && !run.ok && run.error && (
                <Field label="Error">
                  <span className="text-danger">{run.error}</span>
                </Field>
              )}
              <Field label="Last success">{success ? <When run={success} /> : data ? 'Never' : '—'}</Field>
            </dl>
          )}
        </Card>

        {stats.length > 0 && (
          <Card title="Last successful run">
            <dl>
              {stats.map(([key, value]) => (
                <Field key={key} label={STAT_LABELS[key] ?? key}>
                  <span className="tabular-nums">{String(value)}</span>
                </Field>
              ))}
            </dl>
          </Card>
        )}
      </div>
    </div>
  );
};

export default SyncSettingsPage;
